import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router, UrlTree } from '@angular/router';
import { filter } from 'rxjs';

const JOB_PAGE = /^\/embed\/jobs\/[^/]+(\/|$)/;
const SCHOOL_PROFILE_PAGE = /^\/embed\/schools\/[^/]+$/;
const SCHOOL_LIST_PAGE = /^\/embed\/schools(-with-filters)?$/;

/**
 * Keeps a visitor inside the customer's iframe (`/embed/...`): job detail and the
 * public apply flow render under EmbedLayout too, so the customer's website stays
 * around them. Only the owner's school profile may leave — and only when the
 * customer did not integrate school profiles themselves.
 *
 * Must be created before the first NavigationEnd, which is why EmbedLayoutComponent
 * injects it.
 */
@Injectable({ providedIn: 'root' })
export class EmbedNavigationService {
  private readonly router = inject(Router);

  /**
   * Last embed page that is not a job page — where "Zurück" returns to. Never
   * `history.back()`: inside an iframe that can navigate the customer's page.
   */
  private returnUrl: string | null = null;
  /** A `/embed/schools/:id` profile was rendered in this iframe session. */
  private schoolProfileShown = false;
  /** Last school list in this iframe — where a profile's "Zurück" returns to. */
  private schoolListUrl: string | null = null;
  /** The school opened from the list, so the list can scroll back to its card. */
  private openedSchoolId: string | null = null;
  /** Loaded state of a list by its URL, kept while a profile is open. */
  private readonly listSnapshots = new Map<string, unknown>();

  constructor() {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => this.record(e.urlAfterRedirects));
  }

  /**
   * The list or profile the visitor came from, filters and scroll position
   * included. `null` when the iframe was opened directly on a job.
   */
  get backUrl(): UrlTree | null {
    return this.returnUrl ? this.router.parseUrl(this.returnUrl) : null;
  }

  /**
   * The school list a profile was opened from, filters included. `null` when
   * the iframe was opened directly on a profile.
   */
  get schoolListBackUrl(): UrlTree | null {
    return this.schoolListUrl ? this.router.parseUrl(this.schoolListUrl) : null;
  }

  /** Remembers the school a list card opened, for {@link revealOpenedSchool}. */
  openedSchool(id: string): void {
    this.openedSchoolId = id;
  }

  /**
   * Back on the list: scrolls the card of the school opened from it into view.
   * The customer's page scrolls, not the iframe, so wait until their script has
   * resized the iframe back to the list (its `resize` event), or a moment.
   */
  revealOpenedSchool(doc: Document): void {
    const id = this.openedSchoolId;
    this.openedSchoolId = null;
    if (!id) return;
    const win = doc.defaultView;
    const reveal = () =>
      doc
        .querySelector(`a[href="/embed/schools/${encodeURIComponent(id)}"]`)
        ?.scrollIntoView({ block: 'center' });
    if (!win) return reveal();
    const onResize = () => {
      clearTimeout(fallback);
      win.requestAnimationFrame(reveal);
    };
    const fallback = setTimeout(() => {
      win.removeEventListener('resize', onResize);
      reveal();
    }, 600);
    win.addEventListener('resize', onResize, { once: true });
  }

  /** Keeps a list's loaded pages until the visitor comes back to `url`. */
  rememberList(url: string, snapshot: unknown): void {
    this.listSnapshots.set(url, snapshot);
  }

  /** The state {@link rememberList} kept for `url`, once. */
  takeList<T>(url: string): T | null {
    const snapshot = this.listSnapshots.get(url) as T | undefined;
    this.listSnapshots.delete(url);
    return snapshot ?? null;
  }

  jobLink(jobId: string): string[] {
    return ['/embed/jobs', jobId];
  }

  /**
   * Whether the owner's profile can open inside the iframe. Visitors only notice
   * they left the customer's site when the customer did not embed school profiles.
   */
  keepsSchoolProfileInEmbed(): boolean {
    return this.schoolProfileShown;
  }

  private record(url: string): void {
    const path = url.split(/[?#]/)[0];
    if (!path.startsWith('/embed/')) {
      return;
    }
    if (SCHOOL_PROFILE_PAGE.test(path)) {
      this.schoolProfileShown = true;
    }
    if (SCHOOL_LIST_PAGE.test(path)) {
      this.schoolListUrl = url;
    }
    if (!JOB_PAGE.test(path)) {
      this.returnUrl = url;
    }
  }
}
